import {Autocomplete, TextField, TextFieldProps} from '@mui/material';
import isNil from 'lodash/isNil';
import {useMemo} from 'react';

import {useAppSelector} from '../../hooks/redux';
import {SingleMultiSelect} from './SingleMultiSelect';

type Props = Pick<
  TextFieldProps,
  | 'size'
  | 'name'
  | 'disabled'
  | 'fullWidth'
  | 'label'
  | 'error'
  | 'helperText'
  | 'InputProps'
  | 'sx'
> & {
  value?: number | number[] | null;
  nullLabel?: string;
  multiple?: boolean;
  onChange?: (value?: number | number[] | null) => void;
};

export const MachineTypeSelect = ({
  value,
  disabled,
  fullWidth,
  size,
  nullLabel,
  multiple,
  onChange,
  ...props
}: Props) => {
  const machineTypes = useAppSelector(({assets}) => assets.machine_types);
  const nullValue = -1;

  const options = useMemo(() => {
    if (nullLabel && !multiple) {
      return [
        {
          id: nullValue,
          name: nullLabel,
        },
        ...machineTypes,
      ];
    }
    return machineTypes;
  }, [machineTypes, nullLabel, multiple]);

  // Multi-select mode — rendered via SingleMultiSelect (checkbox menu, emit on close)
  if (multiple) {
    return (
      <SingleMultiSelect
        {...props}
        label="Machine Type"
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        multiple
        nullLabel={nullLabel ?? 'All Machine Types'}
        options={machineTypes.map((t) => ({id: t.id, name: t.name ?? ''}))}
        value={value as number[] | null}
        onChange={(v) => onChange?.(v as number[])}
      />
    );
  }

  const selectedOptionId = isNil(value as number | null)
    ? nullValue
    : (value as number);
  const selectedOption = options.find((i) => i.id === selectedOptionId) ?? null;

  return disabled ? (
    <TextField
      {...props}
      value={options.find((i) => i.id === value)?.name ?? ''}
      disabled={disabled}
      fullWidth={fullWidth}
      size={size}
    />
  ) : (
    <Autocomplete
      value={selectedOption ?? null}
      disabled={disabled}
      fullWidth={fullWidth}
      options={options}
      size={size}
      disableClearable={selectedOptionId === nullValue}
      isOptionEqualToValue={(o, v) => o.id === v.id}
      getOptionLabel={(o) => o.name ?? ''}
      renderInput={(params) => (
        <TextField label="Machine Type" {...props} {...params} />
      )}
      renderOption={(props, option) => {
        return (
          <li {...props} key={option.id}>
            {option.name}
          </li>
        );
      }}
      onChange={(_, option) => {
        const v =
          !option || isNil(option.id) || option.id === nullValue
            ? null
            : option.id;
        onChange?.(v);
      }}
    />
  );
};
